import axios from 'axios';

import type { BaseValidation } from '@/components/datamodel/types';
import { relativeUrlResolver } from '@/services/utils';

type AssetValues = Record<string, unknown>;

/** Result of `POST /api/v2/assets/validate`, the same shape as `POST /api/v1/validate` for items. */
interface DamAssetValidationResult {
    validations: Record<string, BaseValidation[]>;
    attributeStates?: Record<string, ('hidden' | 'readonly')[]>;
    calculations?: Record<string, string | number>;
}

interface DamAssetValidationOptions {
    /** Only these attributes are revalidated; `null` validates the whole asset. */
    attributeNames?: string[] | null;
    /** The last validated state, for rules comparing old and new values. */
    oldAsset?: AssetValues;
    attributeStatesOnly?: boolean;
}

const validateUrl = () => relativeUrlResolver('/api/v2/assets/validate');

/** Validates one prospective asset without storing it; a stored asset is merged with its values first. */
const validateDamAsset = async (newAsset: AssetValues, options: DamAssetValidationOptions = {}) =>
    (await axios.post<DamAssetValidationResult>(validateUrl(), {
        newAsset,
        oldAsset: options.oldAsset,
        attributeNames: options.attributeNames ?? null,
        attributeStatesOnly: options.attributeStatesOnly ?? false,
    })).data;

/** Validates several prospective assets in one call; one result per asset, in order. */
const validateDamAssets = async (assets: AssetValues[], options: Omit<DamAssetValidationOptions, 'oldAsset'> = {}) =>
    (await axios.post<DamAssetValidationResult[]>(validateUrl(), {
        assets,
        attributeNames: options.attributeNames ?? null,
        attributeStatesOnly: options.attributeStatesOnly ?? false,
    })).data;

export { validateDamAsset, validateDamAssets };
export type { DamAssetValidationOptions, DamAssetValidationResult };
